import {
  POINT_LABEL_RATING_UNIT_IDS,
  RATING_UNIT_REGISTRY,
  productVersionLabel,
  respondentWordingFor,
  RESEARCH_QUESTION_CEILING,
  isRuleCode,
  lineParams,
  questionRefLabel,
  ruleEntry,
  ruleTemplateValues,
  userCanAct,
  userText,
  withStructuralRefusalParams,
} from "@digital-twins/shared-types";
import type {
  QuestionnaireDraft,
  QuestionnaireOperationEffect,
  QuestionnaireOperationRejectionCode,
  QuestionnaireRefusalParams,
  QuestionnaireValidationIssue,
  ReportProjectionBindingName,
  RuleCode,
  RuleParams,
  SupportedRatingUnitId,
} from "@digital-twins/shared-types";
import { toast } from "sonner";
import i18n, { interfaceLanguageCode } from "@/i18n";
import {
  classifyTransportFailure,
  transportOutcomeUnknown,
} from "@/lib/api/transportFailure";
import { signInUrl } from "@/lib/api/sessionExpiry";

/**
 * Marketer copy for questionnaire validation issues and turn failures.
 *
 * Single rendering authority. Never return `issue.message`, `path`, or `code`
 * for pixels. Codes go to console.error and analytics only.
 *
 * Cache scope: one-shot, not cached. Tables are byte-stable.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const ISSUE_FALLBACK =
  "This question needs a fix before you can send it for review.";

export function draftReferencePrefix(
  draftId: string | null | undefined,
): string | null {
  if (!draftId || !UUID_RE.test(draftId)) {
    return null;
  }
  return draftId.slice(0, 8);
}

export function formatReferenceLine(
  draftId: string | null | undefined,
): string | null {
  const ref = draftReferencePrefix(draftId);
  if (!ref) {
    return null;
  }
  return `${i18n.t("questionnaire.turn.reference", { ns: "app", ref })} ${i18n.t(
    "questionnaire.turn.contactSupport",
    { ns: "app" },
  )}`;
}

export interface IssueCopyContext {
  draftId?: string | null;
  resolvedMinimum?: number;
}

/**
 * A rating scale as a user line names it: its range from the unit registry
 * ("1–5"), which reads the same in every language, and a binary scale by the
 * interface language's own yes and no (the scale-wording pack). The
 * registry's prose label ("1 to 5") stays the model-facing name.
 */
export function ratingScaleUserName(unit: SupportedRatingUnitId): string {
  const { rawMin, rawMax, normalization } = RATING_UNIT_REGISTRY[unit];
  if (normalization.method === "binary") {
    const { yes, no } = respondentWordingFor(interfaceLanguageCode());
    return `${yes}/${no}`;
  }
  return `${rawMin}–${rawMax}`;
}

/**
 * Registry facts a catalog user line names as a param until the catalog
 * reads them by key (`label_too_long`, `point_labels_unsupported_unit`).
 */
const USER_TEXT_PARAMS: RuleParams = {
  pointLabelScales: POINT_LABEL_RATING_UNIT_IDS.map(ratingScaleUserName),
};

/** Untyped: a rule's key exists in the locales that translate it, not in `en`. */
const translate = i18n.t as unknown as (
  key: string,
  options: Record<string, unknown>,
) => string;

/**
 * The user's line for `code`: its rule catalog entry's
 * (`config/questionnaire-rule-catalog.json`), filled from its facts and
 * `params`, in the interface language when that locale translates it
 * (`questionnaire.rule.<code>`; i18next reads the English line otherwise).
 * Undefined for a code the catalog gives no line.
 */
export function ruleCopy(
  code: string,
  params?: RuleParams,
): string | undefined {
  if (!isRuleCode(code)) return undefined;
  const values = { ...USER_TEXT_PARAMS, ...params };
  const english = userText(code, values);
  if (english === undefined) return undefined;
  return translate(`questionnaire.rule.${code}`, {
    ns: "app",
    ...ruleTemplateValues(code, values),
    ...familyNounsInReaderLanguage(code, values),
    defaultValue: english,
  });
}

/**
 * The asset-family noun forms a rule line reads from the family registry, and
 * the form each language's own family nouns (`questionnaire.assetTypes.<family>`)
 * give it. The registry's nouns are English; they stay the English line's.
 */
const FAMILY_NOUN_FORM_IN_LOCALE: Readonly<Record<string, "singular" | "plural">> = {
  "questionnaire-asset-families#families.{family}.nouns.singular": "singular",
  "questionnaire-asset-families#families.{family}.nouns.plural": "plural",
  "questionnaire-asset-families#families.{family}.nouns.limit": "plural",
};

/**
 * For a translated line: each fact that names the family in English, as the
 * reader's language names it, lower-cased for the middle of a sentence (the
 * plural is a title-case heading), as the cap line in the asset panel does.
 */
function familyNounsInReaderLanguage(
  code: RuleCode,
  values: RuleParams,
): Record<string, string> {
  const family = values.family;
  if (typeof family !== "string") return {};
  const nouns: Record<string, string> = {};
  for (const [name, ref] of Object.entries(ruleEntry(code).facts ?? {})) {
    const form = FAMILY_NOUN_FORM_IN_LOCALE[ref];
    if (form) {
      nouns[name] = translate(`questionnaire.assetTypes.${family}.${form}`, {
        ns: "app",
      }).toLocaleLowerCase();
    }
  }
  return nouns;
}

/** The turn line an unmapped failure says: the catalog's generic cause. */
const TURN_FAILED = "turn_failed";

/**
 * Whether the catalog writes `code`'s line as the turn's cause alone (its
 * `today` is `turn_failure`), so the turn's outcome is said before it. A
 * refusal's line (`route_refusal`, a rule's) states its own outcome: the
 * request applied nothing.
 */
function statesCauseOnly(code: string): boolean {
  return isRuleCode(code) && (ruleEntry(code).today ?? []).includes("turn_failure");
}

/**
 * A refusal of PLAN's asset operations: the build and replan routes wrap it
 * (the surface code is `build_plan_stage_failed`, and this code rides in
 * `failureCodes`), so its line is said instead of the wrapper's, from the
 * refused operations' own codes (`assetRefusalCopy`). Whether it is retried
 * is the catalog's verdict on the wire, like every cause's.
 */
const PLAN_ASSET_OPERATION_REJECTED = "plan_asset_operation_rejected";

/**
 * A refused asset removal has two ways out: fix the questions it names (each
 * code's own line), or undo the removal and let the chat remove the item and
 * update them. The chat works on the saved questionnaire and saves the edits
 * waiting before it answers, so its way starts with the undo ("Save and
 * continue" never sends past a refused Save). A toast says it once.
 */
const ASSET_REMOVAL_CHAT_WAY_OUT =
  "Or undo the removal and then ask the chat to remove the item and update those questions.";

/** The refusals only an asset removal raises (the applier's removal cascade). */
const ASSET_REMOVAL_REFUSAL_CODES: ReadonlySet<string> = new Set<
  QuestionnaireOperationRejectionCode
>([
  "asset_subject_removed",
  "asset_mentioned",
  "comparison_below_two",
  "choice_list_below_minimum",
  "tested_asset_rows_emptied",
  "grid_rows_emptied",
]);

/** The Save line when the draft moved under the edits (409). */
export const SAVE_CONFLICT_COPY =
  "This questionnaire was updated while you were editing. Your edits are kept on the latest version. Save again.";

export const SAVE_FAILURE_FALLBACK = "Couldn't save your edits. Try again.";

/** A report chart, as the builder names it in a `report_chart_lost` line. */
const REPORT_CHART_NAMES: Record<ReportProjectionBindingName, string> = {
  quadrantAxes: "positioning map",
  liftBaseline: "lift chart",
  comparison: "comparison table",
  priceCurve: "price curve",
};

/**
 * What the server changed on a question, or on an asset, to keep a Save's
 * references whole. `label` names what changed: "Q3", or the asset's name.
 * Pure bookkeeping (cohort labels, synced participants, a removed ref with no
 * visible trace) says nothing.
 */
export function formatOperationEffectCopy(
  effect: Pick<QuestionnaireOperationEffect, "kind" | "chart" | "module">,
  label: string,
  moduleTitles?: Readonly<Record<string, string>>,
): string | null {
  switch (effect.kind) {
    case "report_chart_lost":
      return effect.chart
        ? `${label}: the report's ${REPORT_CHART_NAMES[effect.chart]} can no longer be drawn from it.`
        : null;
    case "report_module_lost": {
      // C07-4 (#37): the section the report's recipe module made, by the
      // title the stamped report plan gives it.
      const title = effect.module ? moduleTitles?.[effect.module]?.trim() : undefined;
      return title
        ? `${label}: the report no longer has its "${title}" section; no question feeds it now.`
        : `${label}: the report no longer has the section this question fed.`;
    }
    case "asset_choice_removed":
      return `${label}: the choice for the removed item is gone.`;
    case "asset_row_removed":
      return `${label}: the row for the removed item is gone.`;
    case "asset_choice_added":
      return `${label}: the new item was added as a choice.`;
    case "condition_value_pruned":
      return `${label}: its logic no longer checks for an answer you removed.`;
    case "split_groups_cleared":
      return `${label}: the report's groups for it named an answer you removed, so the report splits it by each answer again.`;
    case "choices_from_recopied":
      return `${label}: it shows the updated options of the question it reuses.`;
    case "exposure_anchor_moved":
      return `${label}: the exposure line moved here.`;
    case "asset_binding_released":
      return `${label}: it now comes before twins see anything, so it no longer shows what you're testing.`;
    case "asset_mention_renamed":
      return `${label}: it now names the renamed item.`;
    case "primary_question_moved":
      return `${label}: it is now the study's main question.`;
    case "asset_description_cleared":
      return `${label}: its description came from the file you removed, so it was cleared.`;
    case "price_version_renamed":
      return `${label}: it's the same product at another price, so it took the new name too.`;
    case "escape_moved_last":
      return `${label}: an answer that rules out the others now comes last, where it always stays.`;
    case "escape_flag_removed":
      return `${label}: a ranking ranks every answer, so no answer is kept apart as "none" or "other" any more.`;
    case "brand_roster_followed":
      return `${label}: its brands follow the study's brands, so they changed with them.`;
    case "plan_attribution_cleared":
      return `${label}: with its new answer type it no longer feeds its Summary card or KPI row, and its report label was removed.`;
    default:
      return null;
  }
}

/**
 * Where the builder's Save toasts sit: away from the panel header, so they
 * never cover Save, Discard or Undo while the marketer acts on them.
 */
export const SAVE_TOAST_POSITION = "bottom-right" as const;

/**
 * What an effect changed, as the builder names it: "Q4", or an asset's name,
 * a priced product's with its price (`productVersionLabel`, as its matrix row
 * and the strip read it), so price versions that share the name are told
 * apart. A question the write deleted (a `report_chart_lost` on it) is named
 * by its title from `before`: its old number now names another question
 * (LP-04).
 */
function effectLabel(
  effect: QuestionnaireOperationEffect,
  saved: QuestionnaireDraft,
  before?: QuestionnaireDraft | null,
): string {
  if (effect.assetStableKey) {
    const asset = (saved.testedAssets?.assets ?? []).find(
      (candidate) => candidate.stableKey === effect.assetStableKey,
    );
    const name = asset?.name?.trim();
    return name
      ? productVersionLabel(
          name,
          asset?.type === "product" ? asset.price : undefined,
          respondentWordingFor(saved.contentLanguage),
        )
      : "An item";
  }
  const index = (saved.researchQuestions ?? []).findIndex(
    (question) => question.stableKey === effect.questionStableKey,
  );
  if (index >= 0) return questionRefLabel(index);
  const deletedTitle = (before?.researchQuestions ?? [])
    .find((question) => question.stableKey === effect.questionStableKey)
    ?.title?.trim();
  return deletedTitle ? `"${deletedTitle}"` : "A question";
}

/**
 * What the server changed on other questions or assets to keep a write whole
 * ("Q4: the choice for the removed item is gone"), said once, after it: a
 * builder Save, or a chat turn's revision on its live settle (plan S4.3, L32;
 * a reload says nothing). `saved` is the draft the write left, which numbers
 * the questions and names the assets; `before`, the draft it replaced, names
 * a question the write deleted by its title.
 */
export function announceSaveEffects(
  effects: ReadonlyArray<QuestionnaireOperationEffect> | undefined,
  saved: QuestionnaireDraft | null | undefined,
  before?: QuestionnaireDraft | null,
): void {
  if (!effects?.length || !saved) return;
  const lines = new Set<string>();
  for (const effect of effects) {
    const line = formatOperationEffectCopy(
      effect,
      effectLabel(effect, saved, before),
      saved.reportingPlan?.moduleTitles,
    );
    if (line) lines.add(line);
  }
  if (lines.size === 0) return;
  toast("Saved. We also updated:", {
    description: [...lines].join(" "),
    position: SAVE_TOAST_POSITION,
  });
}

export interface FormattedIssueCopy {
  /** The whole line: `ownText`, then `sharedWayOut` when there is one. */
  text: string;
  /** The line without the way out it shares with related refusals. */
  ownText: string;
  /**
   * A way out several refusals share (an asset removal's chat way), which a
   * toast naming several lines says once. Null for most codes.
   */
  sharedWayOut: string | null;
  mapped: boolean;
  code: string;
  referenceLine: string | null;
}

export function formatIssueCopy(
  issue: Pick<QuestionnaireValidationIssue, "code" | "message"> &
    Partial<Pick<QuestionnaireValidationIssue, "path" | "assetId">> & {
      /** A refusal's typed facts, which fill its line. */
      params?: QuestionnaireRefusalParams;
    },
  context: IssueCopyContext = {},
): FormattedIssueCopy {
  const code = typeof issue.code === "string" ? issue.code : "";
  const line = fillsLine(code, issue.params)
    ? ruleCopy(code, issue.params)
    : undefined;
  const mapped = line !== undefined;
  const ownText = line ?? ISSUE_FALLBACK;
  const sharedWayOut = ASSET_REMOVAL_REFUSAL_CODES.has(code)
    ? ASSET_REMOVAL_CHAT_WAY_OUT
    : null;
  return {
    text: sharedWayOut ? `${ownText} ${sharedWayOut}` : ownText,
    ownText,
    sharedWayOut,
    mapped,
    code,
    // Our failure (no line, or one the user never causes) names the draft
    // for support.
    referenceLine:
      !mapped || ruleEntry(code as RuleCode).severity?.user === "invariant"
        ? formatReferenceLine(context.draftId)
        : null,
  };
}

/**
 * The line for a request that failed in transport (no answer, a timeout, an
 * expired session, an uncoded 404/413/5xx), in the interface language
 * (`questionnaire.transport.*`), and whether to offer Retry: only when the
 * outcome is unknown. Null when the server answered with its own refusal, or
 * the failure is not a request's.
 */
export function transportFailureCopy(
  error: unknown,
): { line: string; retryable: boolean } | null {
  const kind = classifyTransportFailure(error);
  if (kind === null) return null;
  return {
    line: i18n.t(`questionnaire.transport.${kind}`, { ns: "app" }),
    retryable: transportOutcomeUnknown(kind),
  };
}

/**
 * A Sign in action for a request refused because the session expired (B8):
 * it goes to sign-in and back to this page, where the unsaved edits kept in
 * the tab come back (B4). No action for any other failure.
 */
export function signInActionFor(
  error: unknown,
): { action: { label: string; onClick: () => void } } | Record<string, never> {
  if (classifyTransportFailure(error) !== "session_expired") return {};
  return {
    action: {
      label: i18n.t("questionnaire.signIn", { ns: "app" }),
      onClick: () => window.location.assign(signInUrl()),
    },
  };
}

/**
 * The builder's line for a request with no typed write refusal to map: the
 * catalog's line for the body's `code`, else the transport line, else the
 * caller's own `fallback`. Never the server's sentence.
 */
export function requestFailureLine(error: unknown, fallback: string): string {
  const code = readTurnFailureBody(error).code;
  const params = readRequestRefusalParams(error);
  const coded =
    code && fillsLine(code, params) ? ruleCopy(code, params) : undefined;
  return coded ?? transportFailureCopy(error)?.line ?? fallback;
}

/**
 * The body's `params` for its catalog line: names the server fills from data
 * (an audience's name, a column, its answers), never a sentence of its own.
 */
function readRequestRefusalParams(error: unknown): RuleParams {
  const raw = (error as { response?: { data?: { params?: unknown } } })
    ?.response?.data?.params;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const params: Record<string, string | number | ReadonlyArray<string>> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === "string" || typeof value === "number") {
      params[name] = value;
    } else if (
      Array.isArray(value) &&
      value.every((item): item is string => typeof item === "string")
    ) {
      params[name] = value;
    }
  }
  return params;
}

export interface FormattedTurnFailure {
  title: string;
  description: string | null;
  mapped: boolean;
  code: string;
  retryable: boolean;
  /**
   * No answer said whether the turn was queued (a transport failure of its
   * enqueue). Its Retry sends the same turn again (the same client id), which
   * the server answers with the turn it may already have queued.
   */
  outcomeUnknown: boolean;
  /**
   * The upstream stage's own codes, for the console and nothing else. One
   * surface code covers several unrelated failures — `build_author_stage_failed`
   * is raised for a rejected authored question, an unhandled upstream error and
   * a dropped socket alike — and these are what tell them apart. Never shown to
   * the marketer: the copy stays keyed by `code`.
   */
  failureCodes: Array<string>;
}

function readTurnFailureBody(error: unknown): {
  code: string;
  retryable: boolean | undefined;
  failureCodes: Array<string>;
  failureDetails: Array<{
    code: string;
    reasonCode: string;
    params?: QuestionnaireRefusalParams;
  }>;
} {
  // Only typed fields are read: the body's `message` is the server's own
  // sentence, which the builder never shows (catalog lines only).
  const data = (
    error as {
      response?: {
        data?: {
          code?: unknown;
          retryable?: unknown;
          failureCodes?: unknown;
          failureDetails?: unknown;
        };
      };
    }
  )?.response?.data;
  const code = typeof data?.code === "string" ? data.code : "";
  const retryable =
    typeof data?.retryable === "boolean" ? data.retryable : undefined;
  const failureCodes = Array.isArray(data?.failureCodes)
    ? data.failureCodes.filter(
        (failureCode): failureCode is string => typeof failureCode === "string",
      )
    : [];
  const failureDetails = Array.isArray(data?.failureDetails)
    ? data.failureDetails.flatMap((detail: unknown) => {
        const record = detail as Record<string, unknown> | null;
        if (
          typeof record?.code !== "string" ||
          typeof record.reasonCode !== "string"
        ) {
          return [];
        }
        return [
          {
            code: record.code,
            reasonCode: record.reasonCode,
            ...withStructuralRefusalParams(record.params),
          },
        ];
      })
    : [];
  return { code, retryable, failureCodes, failureDetails };
}

/** Whether `params` (and the builder's own facts) fill every param `code`'s user line names. */
function fillsLine(
  code: string,
  params?: QuestionnaireRefusalParams | RuleParams,
): boolean {
  if (!isRuleCode(code)) return false;
  const values: Record<string, unknown> = { ...USER_TEXT_PARAMS, ...params };
  return lineParams(code, "user").every((name) => values[name] !== undefined);
}

/**
 * The line for refused operations on what the study tests: the catalog's
 * `plan_asset_operation_rejected` template, with each refusal's own line as
 * its reasons. Undefined when a refusal is not the user's to act on, or has
 * no line it can fill: our failure, which reads as the generic one.
 */
function assetRefusalCopy(
  details: ReturnType<typeof readTurnFailureBody>["failureDetails"],
): string | undefined {
  const refusals = details.filter(
    (detail) => detail.code === PLAN_ASSET_OPERATION_REJECTED,
  );
  if (
    refusals.length === 0 ||
    !refusals.every(
      (detail) =>
        isRuleCode(detail.reasonCode) &&
        userCanAct(detail.reasonCode) &&
        fillsLine(detail.reasonCode, detail.params),
    )
  ) {
    return undefined;
  }
  const lines = refusals.map((detail) =>
    ruleCopy(detail.reasonCode, detail.params),
  );
  if (lines.some((line) => line === undefined)) return undefined;
  return ruleCopy(PLAN_ASSET_OPERATION_REJECTED, {
    reasons: [...new Set(lines)].join(" "),
  });
}

/**
 * The one renderer of how a turn ended short: a failed or refused turn
 * (`committed` false), or one that stopped after one of its commits landed
 * (`committed` true, #3). The catalog line states the cause; the turn's
 * commit fact states the outcome, said first. A transport line states
 * neither: nobody knows whether the turn was queued.
 */
export function formatTurnFailureCopy(
  error: unknown,
  draftId: string | null | undefined,
  { committed = false }: { committed?: boolean } = {},
): FormattedTurnFailure {
  const {
    code,
    retryable: wireRetryable,
    failureCodes,
    failureDetails,
  } = readTurnFailureBody(error);
  // What the builder shows is catalog text, never the server's sentence.
  const lineCode =
    code === PLAN_ASSET_OPERATION_REJECTED ||
    failureCodes.includes(PLAN_ASSET_OPERATION_REJECTED)
      ? PLAN_ASSET_OPERATION_REJECTED
      : code;
  const ownLine =
    lineCode === PLAN_ASSET_OPERATION_REJECTED
      ? assetRefusalCopy(failureDetails)
      : fillsLine(code)
        ? ruleCopy(code)
        : undefined;
  // No typed refusal: a transport failure has its own line, and a Retry
  // when nobody can tell whether the turn was queued.
  const transport = ownLine === undefined ? transportFailureCopy(error) : null;
  if (transport) {
    return {
      title: transport.line,
      description: null,
      mapped: true,
      code,
      retryable: transport.retryable,
      outcomeUnknown: transport.retryable,
      failureCodes,
    };
  }
  // A line that states its own outcome (a refusal applied nothing) cannot
  // describe a turn that committed: that turn reads as an unmapped one.
  const mapped =
    ownLine !== undefined && (!committed || statesCauseOnly(lineCode));
  const causeCode = mapped ? lineCode : TURN_FAILED;
  const cause = (mapped ? ownLine : ruleCopy(TURN_FAILED)) ?? "";
  const title = statesCauseOnly(causeCode)
    ? `${i18n.t(
        committed
          ? "questionnaire.turn.outcome.savedRestDidNotRun"
          : "questionnaire.turn.outcome.nothingSaved",
        { ns: "app" },
      )} ${cause}`
    : cause;
  // The cause's one verdict, as its owner put it on the wire (the rule
  // catalog's `retryable`, or the stage's own): the same for a turn that
  // committed first, whose Retry resumes what the commit left undone.
  const retryable = wireRetryable === true;
  const referenceLine = formatReferenceLine(draftId);
  const descriptionParts: Array<string> = [];
  if (retryable && mapped) {
    descriptionParts.push(i18n.t("questionnaire.turn.retry", { ns: "app" }));
  }
  if (!mapped || wireRetryable === false) {
    if (referenceLine) {
      descriptionParts.push(referenceLine);
    } else if (!mapped) {
      descriptionParts.push(
        i18n.t("questionnaire.turn.contactSupport", { ns: "app" }),
      );
    }
  }
  return {
    title,
    description:
      descriptionParts.length > 0 ? descriptionParts.join(" ") : null,
    mapped,
    code,
    retryable,
    outcomeUnknown: false,
    failureCodes,
  };
}

export function formatIssueBannerLine(
  issues: Array<
    Pick<QuestionnaireValidationIssue, "code" | "message" | "path">
  >,
): string {
  const count = issues.length;
  if (count === 1) {
    return "1 question needs a fix before review.";
  }
  return `${count} questions need a fix before review.`;
}

export function formatUnscopedBannerLine(
  step: "audience" | "workspace" | "assets" | "review",
  issue?: QuestionnaireValidationIssue,
  context: IssueCopyContext = {},
): string {
  if (step === "assets") {
    return "Open the assets step to finish this.";
  }
  if (step === "workspace") {
    return `Keep the questionnaire to ${RESEARCH_QUESTION_CEILING} questions or fewer.`;
  }
  if (step === "audience") {
    return "Open the audience step to finish this.";
  }
  // No step to open: the issue's own line (its rule catalog entry's) says what to do.
  const copy = issue ? formatIssueCopy(issue, context) : null;
  return copy?.mapped
    ? copy.text
    : "This questionnaire needs a fix before review.";
}
